/**
 * Shape → Build → Release → Learn, with the two approvals drawn where they
 * fall: approval one (the brief) closes Shape, approval two (the release)
 * closes Release. The full strip heads a change page; the compact strip sits
 * in a list row; the legend explains the four stages once, on the list page.
 */
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  APPROVALS,
  STAGES,
  STAGE_LABEL,
  STAGE_WHAT,
  type ChangeState,
  type Stage,
} from '@/lib/changes/model';
import { StateBadge } from './changeBadges';

/** The approval that closes a stage, if one does. */
export function approvalAfter(stage: Stage): (typeof APPROVALS)['one' | 'two'] | null {
  if (stage === 'shape') return APPROVALS.one;
  if (stage === 'release') return APPROVALS.two;
  return null;
}

function Connector({ approval }: { approval: (typeof APPROVALS)['one' | 'two'] | null }) {
  return (
    <div className="flex flex-col items-center justify-center self-stretch px-1 text-slate-400 dark:text-slate-500 sm:w-28">
      {approval ? (
        <span className="text-center text-[11px] font-medium leading-tight text-violet-700 dark:text-violet-300">
          {approval.label}
          <br />
          <span className="font-normal text-slate-500 dark:text-slate-400">{approval.what}</span>
        </span>
      ) : (
        <span className="h-[1.6rem]" aria-hidden />
      )}
      <span aria-hidden className="mt-1 flex w-full items-center">
        <span className="h-px flex-1 bg-current" />
        <ArrowRight className="-ml-1 h-3.5 w-3.5 shrink-0" />
      </span>
    </div>
  );
}

export function StageStrip({
  states,
  current,
  className,
}: {
  states: Record<Stage, ChangeState>;
  current: Stage;
  className?: string;
}) {
  return (
    <ol
      aria-label="Stages"
      className={cn('flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-0', className)}
    >
      {STAGES.map((stage, i) => {
        const isCurrent = stage === current;
        const approval = approvalAfter(stage);
        return (
          <li key={stage} className="contents">
            <div
              data-stage={stage}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'flex flex-1 flex-col gap-1.5 rounded-xl border p-3',
                isCurrent
                  ? 'border-violet-300 bg-violet-50 ring-1 ring-inset ring-violet-500/30 dark:border-violet-700 dark:bg-violet-950/30'
                  : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  <span className="mr-1.5 text-xs font-medium text-slate-400 dark:text-slate-500">{i + 1}</span>
                  {STAGE_LABEL[stage]}
                </span>
                <StateBadge state={states[stage]} />
              </div>
              <p className="text-xs leading-snug text-slate-500 dark:text-slate-400">{STAGE_WHAT[stage]}</p>
            </div>
            {i < STAGES.length - 1 && (
              <div className="hidden sm:flex">
                <Connector approval={approval} />
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** The four words in a row, the current one lit — for a list row. */
export function StageStripCompact({ current, states }: { current: Stage; states: Record<Stage, ChangeState> }) {
  const currentIndex = STAGES.indexOf(current);
  return (
    <ol aria-label="Stages" className="flex items-center gap-1 text-[11px] font-medium">
      {STAGES.map((stage, i) => {
        const past = i < currentIndex;
        const isCurrent = i === currentIndex;
        return (
          <li key={stage} className="flex items-center gap-1">
            <span
              data-stage={stage}
              aria-current={isCurrent ? 'step' : undefined}
              title={`${STAGE_LABEL[stage]}: ${states[stage]}`}
              className={cn(
                'rounded-full px-2 py-0.5',
                isCurrent && 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
                past && 'text-emerald-700 dark:text-emerald-400',
                !isCurrent && !past && 'text-slate-400 dark:text-slate-500',
              )}
            >
              {STAGE_LABEL[stage]}
            </span>
            {i < STAGES.length - 1 && <ArrowRight aria-hidden className="h-3 w-3 text-slate-300 dark:text-slate-600" />}
          </li>
        );
      })}
    </ol>
  );
}

/** How a change moves — the four stages and the two approvals, said once. */
export function StageLegend({ className }: { className?: string }) {
  return (
    <div className={cn('rounded-xl border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-900/50', className)}>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">How a change moves</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        Four stages, two approvals. The owner approves <strong className="font-medium text-slate-800 dark:text-slate-200">{APPROVALS.one.what}</strong> at the end of Shape and{' '}
        <strong className="font-medium text-slate-800 dark:text-slate-200">{APPROVALS.two.what}</strong> at the end of Release. Nothing in between asks a person to read code.
      </p>
      <ol className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {STAGES.map((stage, i) => {
          const approval = approvalAfter(stage);
          return (
            <li key={stage} className="text-sm">
              <div className="font-semibold text-slate-900 dark:text-slate-100">
                <span className="mr-1.5 text-xs font-medium text-slate-400 dark:text-slate-500">{i + 1}</span>
                {STAGE_LABEL[stage]}
              </div>
              <p className="mt-0.5 text-xs leading-snug text-slate-500 dark:text-slate-400">{STAGE_WHAT[stage]}</p>
              {approval && (
                <p className="mt-1.5 text-xs text-violet-700 dark:text-violet-300">
                  <span className="font-medium">{approval.label}, {approval.what}.</span> {approval.how}
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
